import type { AnswerSelection } from './components/FilterDrawer';
import { createAnswerGroup } from './buildAnswerGroups';
import { parseDirectReportsId } from './buildTeamNodes';
import type { AnswerGroupContext } from './answerGroupSummaries';
import type { AnswerGroup, NewReportData, ReportResponse } from './types';

export interface ReportFilters {
  teamIds: Set<string>;
  answerSelections: AnswerSelection[];
}

export interface AdHocGroupConfig {
  type: 'byAnswer';
  answerSelections: AnswerSelection[];
  teamIds?: Set<string>;
  label?: string;
}

function isEmptyFilter(filters: ReportFilters): boolean {
  return filters.teamIds.size === 0 && filters.answerSelections.length === 0;
}

function getDefaultView(tree: AnswerGroup[]): AnswerGroup[] {
  return tree.filter((group) => group.isDefault);
}

function getRespondentIdsForTeams(teamIds: Set<string>, context: AnswerGroupContext): Set<string> {
  const visited = new Set<string>();
  const respondentIds = new Set<string>();
  const visit = (selection: string) => {
    if (visited.has(selection)) return;
    visited.add(selection);
    const directId = parseDirectReportsId(selection);
    const team = context.teamsById.get(directId ?? selection);
    for (const id of team?.directMembers ?? []) respondentIds.add(String(id));
    if (!directId) (team?.memberTeams ?? []).forEach((id) => visit(String(id)));
  };
  teamIds.forEach(visit);
  return respondentIds;
}

/** OR within a question, AND between questions. Compile once, not for every response. */
function createAnswerMatcher(answerSelections: AnswerSelection[]) {
  const byQuestion = new Map<string, AnswerSelection[]>();
  for (const selection of answerSelections) {
    const selections = byQuestion.get(selection.questionId) ?? [];
    selections.push(selection);
    byQuestion.set(selection.questionId, selections);
  }
  return (response: ReportResponse): boolean =>
    [...byQuestion].every(([questionIndex, selections]) =>
      (response.messages ?? []).some(
        (message) =>
          message.questionDefinitionIndex === questionIndex &&
          selections.some(
            (selection) =>
              (selection.statementId === undefined || String(message.statementIndex) === selection.statementId) &&
              message.value?.split(',').some((value) => value.trim() === selection.optionId),
          ),
      ),
    );
}

export function filterReportData(context: AnswerGroupContext, filters: ReportFilters): NewReportData {
  if (isEmptyFilter(filters)) return context.data;
  const eligible = filters.teamIds.size ? getRespondentIdsForTeams(filters.teamIds, context) : null;
  const matches = createAnswerMatcher(filters.answerSelections);
  const responses = context.data.responses.filter(
    (response) => (!eligible || eligible.has(String(response.respondentId))) && matches(response),
  );
  const respondentIds = filters.answerSelections.length
    ? new Set(responses.map((response) => String(response.respondentId)))
    : eligible;
  return {
    ...context.data,
    responses,
    respondents: context.data.respondents.filter(
      (respondent) => !respondentIds || respondentIds.has(String(respondent.id)),
    ),
  };
}

function pruneTeamTree(group: AnswerGroup, selectedIds: Set<string>, context: AnswerGroupContext): AnswerGroup | null {
  if (group.teamId && selectedIds.has(group.teamId)) return group;
  const includeDirect = group.teamId !== undefined && selectedIds.has(`direct:${group.teamId}`);
  const children = group.children
    .map((child) => pruneTeamTree(child, selectedIds, context))
    .filter((child): child is AnswerGroup => Boolean(child));
  if (children.length === 0 && !includeDirect) return null;
  return createAnswerGroup(context, {
    id: group.id,
    label: group.label,
    type: group.type,
    teamId: group.teamId,
    teamNames: group.teamNames,
    directRespondentIds: includeDirect ? group.directRespondentIds : [],
    directResponseIds: includeDirect ? group.directResponseIds : [],
    children,
  });
}

export function getAdHocGroupKey(config: AdHocGroupConfig): string {
  const answers = [
    ...new Set(
      config.answerSelections.map((selection) =>
        JSON.stringify([selection.questionId, selection.statementId ?? null, selection.optionId]),
      ),
    ),
  ].sort();
  return JSON.stringify([config.type, [...(config.teamIds ?? [])].sort(), answers]);
}

export function calculateAdHocGroup(config: AdHocGroupConfig, context: AnswerGroupContext): AnswerGroup | null {
  if (!config.answerSelections.length) return null;
  const filtered = filterReportData(context, {
    teamIds: config.teamIds ?? new Set(),
    answerSelections: config.answerSelections,
  });
  return createAnswerGroup(context, {
    id: `byAnswer:${getAdHocGroupKey(config)}`,
    label: config.label ?? context.labels.byAnswer,
    type: 'byAnswer',
    directRespondentIds: filtered.responses.map((response) => String(response.respondentId)),
    directResponseIds: filtered.responses.map((response) => response.id),
  });
}

export function applyFilters(
  tree: AnswerGroup[],
  filters: ReportFilters,
  context: AnswerGroupContext,
  getAdHocGroup: (config: AdHocGroupConfig) => AnswerGroup | null,
): AnswerGroup[] {
  if (filters.answerSelections.length) {
    const group = getAdHocGroup({
      type: 'byAnswer',
      answerSelections: filters.answerSelections,
      teamIds: filters.teamIds,
    });
    return group ? [group] : [];
  }
  if (filters.teamIds.size) {
    return tree
      .map((group) => pruneTeamTree(group, filters.teamIds, context))
      .filter((group): group is AnswerGroup => Boolean(group));
  }
  return getDefaultView(tree);
}
